import React, { useEffect, useMemo, useState } from "react";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";

import CustomPagination from "../../../../../core/common/customPagination/CustomPagination";
import ImageWithBasePath from "../../../../../core/common/imageWithBasePath";
import { ROW_LIMIT_DEFAULT } from "../constants/leadsTransfer.constants";

interface LeadTransferHistoryTableProps {
  rows: any[];
  loading: boolean;
}

const UserCell: React.FC<{ name: string; image?: string }> = ({ name, image }) => (
  <div className="d-flex align-items-center">
    <span
      className="avatar avatar-sm border rounded-circle d-flex align-items-center justify-content-center bg-light text-dark fw-bold overflow-hidden"
      style={{ width: 32, height: 32, fontSize: 12 }}
    >
      {image && image.trim() !== "" && image.trim() !== "null" ? (
        <ImageWithBasePath src={image} className="img-fluid rounded-circle" alt={name} />
      ) : (
        <span>{name?.charAt(0)?.toUpperCase() || "U"}</span>
      )}
    </span>
    <span className="ms-2 fw-medium" style={{ fontSize: 13 }}>
      {name || "—"}
    </span>
  </div>
);

const LeadTransferHistoryTable: React.FC<LeadTransferHistoryTableProps> = ({
  rows,
  loading,
}) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(ROW_LIMIT_DEFAULT);

  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  useEffect(() => {
    setPage(1);
  }, [total, limit]);

  const pagedRows = useMemo(
    () => rows.slice((page - 1) * limit, page * limit),
    [rows, page, limit],
  );

  const columns: ColumnsType<any> = [
    {
      title: "Sr No.",
      key: "srNo",
      width: 70,
      align: "center",
      fixed: "left",
      render: (_, __, index) => (
        <span style={{ fontSize: 12, fontWeight: 500, color: "#6b7280" }}>
          {(page - 1) * limit + index + 1}
        </span>
      ),
    },
    {
      title: "Lead Id",
      dataIndex: "lead_id",
      key: "lead_id",
      width: 110,
      render: (text: any) => (
        <span style={{ fontSize: 13, fontWeight: 500 }}>{text ?? "—"}</span>
      ),
    },
    {
      title: "Lead Name",
      dataIndex: "Name",
      key: "Name",
      width: 180,
      ellipsis: true,
      render: (text: string) => <span style={{ fontSize: 13 }}>{text || "—"}</span>,
    },
    {
      title: "Phone",
      dataIndex: "MobileNo",
      key: "MobileNo",
      width: 150,
      render: (text: string) => <span style={{ fontSize: 13 }}>{text || "—"}</span>,
    },
    {
      title: "Last Pipeline",
      dataIndex: "Stage",
      key: "Stage",
      width: 160,
      ellipsis: true,
      render: (text: string) => <span style={{ fontSize: 13 }}>{text || "—"}</span>,
    },
    {
      title: "Transfer Date",
      dataIndex: "TransferDate",
      key: "TransferDate",
      width: 180,
      sorter: (a: any, b: any) =>
        new Date(a.TransferDate).getTime() - new Date(b.TransferDate).getTime(),
      render: (text: string) =>
        text ? (
          <span style={{ fontSize: 13 }}>
            {moment(text).format("DD-MM-YYYY hh:mm A")}
          </span>
        ) : (
          "—"
        ),
    },
    {
      title: "Transfer From",
      dataIndex: "From",
      key: "From",
      width: 200,
      render: (text: string, record: any) => (
        <UserCell name={text} image={record.FromImage} />
      ),
    },
    {
      title: "Transfer To",
      dataIndex: "To",
      key: "To",
      width: 200,
      render: (text: string, record: any) => (
        <UserCell name={text} image={record.ToImage} />
      ),
    },
  ];

  if (!loading && total === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-white"
        style={{ minHeight: 320, borderRadius: 12, border: "1px solid #e2e8f0" }}
      >
        <div className="text-center">
          <i className="ti ti-arrows-exchange" style={{ fontSize: 48, color: "#cbd5e1" }} />
          <h5 className="mt-3 mb-2 fw-semibold">No transfer history</h5>
          <p className="text-muted mb-0">
            Transferred leads for the selected form will appear here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white"
      style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0" }}
    >
      <Table
        className="custom-data-table"
        columns={columns}
        dataSource={pagedRows}
        rowKey={(record) =>
          `${record?.lead_id ?? "row"}-${record?.TransferDate ?? ""}-${record?.From ?? ""}-${record?.To ?? ""}`
        }
        size="middle"
        loading={loading}
        pagination={false}
        scroll={{ x: 1200 }}
        rowClassName={(_, index) =>
          index % 2 === 0 ? "data-table-row-light" : "data-table-row-dark"
        }
      />
      <CustomPagination
        currentPage={page}
        totalPages={totalPages}
        total={total}
        limit={limit}
        hasNextPage={page < totalPages}
        hasPrevPage={page > 1}
        onPageChange={setPage}
        onLimitChange={(l) => {
          setLimit(l);
          setPage(1);
        }}
      />
    </div>
  );
};

export default LeadTransferHistoryTable;
